import axios from "axios";
import { track } from "./analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// withCredentials: the session is an HttpOnly cookie now, so the browser attaches
// it — and there is nothing here for a script to read. That is the entire point:
// `localStorage.getItem("costock_token")` used to hand any script on the origin a
// week-long session, so one XSS or one bad transitive dependency was a full account
// takeover. Nothing in this file may ever read or write a session token again.
const client = axios.create({ baseURL: API, withCredentials: true });

/** Carry `investify_*` browser storage over to `costock_*`, once.
 *
 * The keys were renamed with the product. Renaming them without this would sign every
 * existing session out, re-ask for cookie consent, and reset everyone's language and
 * currency — a rebrand should not look like a data loss. Runs at module load, before
 * AuthContext reads the token, and only copies keys that are not already set so a
 * later legitimate value is never clobbered by a stale one.
 *
 * Safe to delete once no browser can still be holding the old keys.
 */
(function migrateStorageKeys() {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const oldKey = localStorage.key(i);
      if (!oldKey || !oldKey.startsWith("investify_")) continue;
      const newKey = `costock_${oldKey.slice("investify_".length)}`;
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, localStorage.getItem(oldKey));
      }
    }
    Object.keys(localStorage)
      .filter((k) => k.startsWith("investify_"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // Private mode with storage disabled — nothing to migrate, nothing to do.
  }
})();

/** A stable id for this browser, created once and kept.
 *
 * Half of the "machine" side of a ban (see backend/bans.py) — the other half is a
 * hash of headers the request already sends, which survives this being cleared.
 * Deliberately a plain random id and nothing more: it identifies a browser we are
 * already serving, not a person across the web, so there is no fingerprinting and
 * nothing to disclose beyond what the cookie banner already covers.
 */
function deviceId() {
  try {
    let id = localStorage.getItem("costock_device");
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID())
        || Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("costock_device", id);
    }
    return id;
  } catch {
    return "";   // private mode with storage disabled — the header is simply absent
  }
}

/** The CSRF token, read back out of the cookie the server set.
 *
 * Deliberately readable: the browser will attach our session cookie to a request
 * from any site, but the same-origin policy stops another site *reading* this one,
 * so echoing it back is proof the request came from a page on our origin. See
 * backend/csrf.py.
 */
function csrfToken() {
  try {
    const hit = document.cookie.split("; ").find((c) => c.startsWith("costock_csrf="));
    return hit ? decodeURIComponent(hit.slice("costock_csrf=".length)) : "";
  } catch {
    return "";
  }
}

const UNSAFE = ["post", "put", "patch", "delete"];

client.interceptors.request.use((config) => {
  const dev = deviceId();
  if (dev) config.headers["X-Device-Id"] = dev;
  if (UNSAFE.includes((config.method || "get").toLowerCase())) {
    const token = csrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

/** One-time migration off the localStorage token.
 *
 * Sessions that predate the cookie deploy are still sitting in localStorage. Rather
 * than signing everybody out, the app trades that token for cookies once and then
 * deletes it. The backend accepts this only while STRICT_COOKIE_ONLY is off; after
 * the cutover it answers 410 and we simply drop the token, which lands the user on
 * the login screen exactly as they would have been anyway.
 *
 * Safe to delete this function, and the `migrating` gate below, once the cutover is
 * done and no browser can still be holding an old token.
 */
let migrating = null;
function migrateLegacyToken() {
  let legacy = null;
  try { legacy = localStorage.getItem("costock_token"); } catch { return null; }
  if (!legacy) return null;
  if (migrating) return migrating;
  migrating = axios
    .post(`${API}/auth/exchange`, null, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${legacy}` },
    })
    .catch(() => {})
    .finally(() => {
      // Dropped either way. On success the cookies have replaced it; on failure it
      // was not going to work for much longer regardless, and leaving it in
      // localStorage is the exposure this whole change exists to remove.
      try { localStorage.removeItem("costock_token"); } catch {}
      migrating = null;
    });
  return migrating;
}

/** Renew the access cookie, at most one request at a time.
 *
 * The access token is minutes long, so 401s are routine rather than exceptional.
 * Everything in flight when one arrives would otherwise fire its own refresh, and
 * because refresh tokens are single-use, the second one through would look like a
 * replay and revoke the whole family — signing the user out for the crime of having
 * two requests on the page. Hence the shared promise.
 */
let refreshing = null;
function refreshSession() {
  if (!refreshing) {
    refreshing = axios
      .post(`${API}/auth/refresh`, null, {
        withCredentials: true,
        // Raw axios, so the client interceptor above does not run — the header has
        // to be set by hand. /auth/refresh is cookie-authenticated and therefore
        // CSRF-protected like any other write.
        headers: { "X-CSRF-Token": csrfToken() },
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

const signOut = () => {
  try { localStorage.removeItem("costock_user"); } catch {}
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
};

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    const { response, config } = err;
    if (!response || response.status !== 401 || !config) return Promise.reject(err);

    // Never try to refresh the refresh itself, and never loop.
    const url = config.url || "";
    if (config.__retried || url.includes("/auth/refresh") || url.includes("/auth/exchange")) {
      if (!config.__probe) signOut();
      return Promise.reject(err);
    }

    try {
      await refreshSession();
    } catch {
      // No usable refresh cookie — this is a real sign-out, not an expired access token.
      if (!config.__probe) signOut();
      return Promise.reject(err);
    }
    config.__retried = true;
    return client(config);
  }
);

// Product events for Google Analytics (lib/analytics.js). Recorded here, on the
// one path every screen uses, rather than in each dialog that happens to call these.
// `track` does nothing without consent, and no event carries a ticker, an amount,
// an e-mail address or an id — only what kind of thing happened.
const tracked = (event, params) => (res) => { track(event, params); return res; };

export const api = {
  // auth (email/password + TOTP)
  register: (payload) => client.post("/auth/register", payload),
  login: (payload) => client.post("/auth/login", payload),
  // First authenticator setup is the end of sign-up; the code check is a sign-in.
  verifyTotpSetup: (payload) => client.post("/auth/totp/verify-setup", payload)
    .then(tracked("sign_up", { method: "password" })),
  verifyTotp: (payload) => client.post("/auth/totp/verify", payload)
    .then(tracked("login", { method: "password" })),
  // __probe: a 401 here means "not signed in", which is a legitimate answer on a
  // public page. Without this the boot check would bounce every logged-out visitor
  // to the login screen. The client can no longer tell whether it holds a session —
  // the cookie is HttpOnly — so asking and accepting "no" is the only way.
  me: () => client.get("/auth/me", { __probe: true }),
  refreshSession: () => client.post("/auth/refresh"),
  migrateLegacyToken,
  logout: () => client.post("/auth/logout"),
  updateSettings: (payload) => client.put("/auth/settings", payload),
  countries: () => client.get("/auth/countries"),
  requestEmailChange: (payload) => client.post("/auth/email-change/request", payload),
  confirmEmailChange: (token) => client.post("/auth/email-change/confirm", { token }),
  cancelEmailChange: () => client.post("/auth/email-change/cancel"),
  notificationPrefs: () => client.get("/notifications/preferences"),
  setNotificationPrefs: (preferences) => client.put("/notifications/preferences", { preferences }),
  adminPreview: () => client.get("/admin/preview"),
  setAdminPreview: (mode) => client.post("/admin/preview", { mode }),
  // market
  search: (q) => client.get(`/search`, { params: { q } }),
  quote: (t) => client.get(`/quote/${t}`),
  fx: (base, quote) => client.get(`/fx`, { params: { base, quote } }),
  history: (t, period, interval) => client.get(`/history/${t}`, { params: { period, interval } }),
  // A chart range button (1d, 1w, 1m, 1y, 5y, all): the server picks the candle
  // interval, so short ranges come back intraday (`intraday: true`).
  chartHistory: (t, range) => client.get(`/history/${t}`, { params: { range } }),
  news: (t) => client.get(`/news/${t}`),
  instrumentDividends: (t) => client.get(`/instrument/${t}/dividends`),
  instrumentBenchmark: (t, range, symbol) => client.get(`/instrument/${t}/benchmark`, { params: { range, symbol } }),
  fundamentals: (t) => client.get(`/instrument/${t}/fundamentals`),
  analysis: (t) => client.get(`/instrument/${t}/analysis`),
  earnings: (t) => client.get(`/instrument/${t}/earnings`),
  newsFeed: () => client.get(`/feed/news`),
  allocation: () => client.get(`/analytics/allocation`),
  benchmark: (period, symbol) => client.get(`/analytics/benchmark`, { params: { period, symbol } }),
  risk: (period) => client.get(`/analytics/risk`, { params: { period } }),
  dividendsByYear: () => client.get(`/analytics/dividends-by-year`),
  // watchlist
  watchlist: () => client.get("/watchlist"),
  addWatch: (payload) => client.post("/watchlist", payload).then(tracked("watchlist_add")),
  removeWatch: (t) => client.delete(`/watchlist/${t}`),
  // transactions / portfolio
  holdings: () => client.get("/holdings"),
  buy: (payload) => client.post("/transactions/buy", payload)
    .then(tracked("transaction_recorded", { transaction_type: "buy" })),
  sell: (payload) => client.post("/transactions/sell", payload)
    .then(tracked("transaction_recorded", { transaction_type: "sell" })),
  updateBuy: (id, payload) => client.put(`/transactions/buy/${id}`, payload),
  updateSell: (id, payload) => client.put(`/transactions/sell/${id}`, payload),
  availableLots: (ticker, broker) => client.get("/transactions/available-lots", { params: { ticker, broker } }),
  brokers: () => client.get("/brokers"),
  transactions: (params) => client.get("/transactions", { params }),
  deleteTransaction: (id) => client.delete(`/transactions/${id}`),
  // dividends & analytics
  dividends: (params) => client.get("/dividends", { params }),
  analyticsSummary: () => client.get("/analytics/summary"),
  portfolioTimeseries: (period) => client.get("/analytics/portfolio-timeseries", { params: { period } }),
  // alerts & notifications
  alerts: () => client.get("/alerts"),
  createAlert: (payload) => client.post("/alerts", payload).then(tracked("alert_created")),
  updateAlert: (id, payload) => client.put(`/alerts/${id}`, payload),
  deleteAlert: (id) => client.delete(`/alerts/${id}`),
  checkAlerts: () => client.post("/alerts/check-now"),
  notifications: () => client.get("/notifications"),
  markNotificationsRead: () => client.post("/notifications/read"),
  deleteNotification: (id) => client.delete(`/notifications/${id}`),
  // tax
  taxEstimate: (params) => client.get("/analytics/tax-estimate", { params }),
  // insurance policies (fund-linked pension/life insurance, e.g. ERGO Renten Chance)
  insurancePolicies: () => client.get("/insurance-policies"),
  createInsurancePolicy: (payload) => client.post("/insurance-policies", payload),
  updateInsurancePolicy: (id, payload) => client.put(`/insurance-policies/${id}`, payload),
  deleteInsurancePolicy: (id) => client.delete(`/insurance-policies/${id}`),
  insurancePolicyTaxScenarios: (id, params) => client.get(`/insurance-policies/${id}/tax-scenarios`, { params }),
  // investors / social
  investors: () => client.get("/investors"),
  investor: (id) => client.get(`/investors/${id}`),
  investorBenchmark: (id, period, symbol) =>
    client.get(`/investors/${id}/benchmark`, { params: { period, symbol } }),
  myInvestorProfile: () => client.get("/investors/me"),
  updateInvestorProfile: (payload) => client.put("/investors/me", payload),
  sharePosition: (txId) => client.post(`/investors/share/${txId}`),
  unsharePosition: (txId) => client.delete(`/investors/share/${txId}`),
  saveCommentary: (txId, payload) => client.put(`/investors/commentary/${txId}`, payload),
  followInvestor: (id) => client.post(`/investors/${id}/follow`),
  unfollowInvestor: (id) => client.delete(`/investors/${id}/follow`),
  feed: () => client.get("/feed"),
  positionComments: (txId) => client.get(`/positions/${txId}/comments`),
  addComment: (txId, payload) => client.post(`/positions/${txId}/comments`, payload),
  moderationQueue: () => client.get("/moderation/comments"),
  moderateComment: (id, payload) => client.post(`/moderation/comments/${id}`, payload),
  // billing
  billingConfig: () => client.get("/billing/config"),
  subscription: () => client.get("/billing/subscription"),
  startCheckout: (plan) => client.post("/billing/checkout", { plan }),
  billingPortal: () => client.post("/billing/portal"),
  invoices: () => client.get("/billing/invoices"),
  // admin
  adminUsers: (q) => client.get("/admin/users", { params: q ? { q } : {} }),
  adminSetRole: (id, role) => client.put(`/admin/users/${id}/role`, { role }),
  adminSetInvestorFlags: (id, payload) => client.put(`/admin/users/${id}/investor`, payload),
  adminDeleteUser: (id, confirmEmail) => client.delete(`/admin/users/${id}`, { params: { confirm_email: confirmEmail } }),
  adminAudit: (params) => client.get("/admin/audit", { params }),
  adminStats: () => client.get("/admin/stats"),
  banPreview: (id) => client.get(`/admin/users/${id}/ban-preview`),
  banUser: (id, payload, confirmEmail) =>
    client.post(`/admin/users/${id}/ban`, payload, { params: { confirm_email: confirmEmail } }),
  unbanUser: (id) => client.post(`/admin/users/${id}/unban`),
  adminBans: () => client.get("/admin/bans"),
  // site / legal / consent
  // `lang` picks which stored translation comes back — a legal page is one record
  // holding both languages, so this is a read-side choice, not a separate document.
  // `adminView` additionally returns every language body for the editor.
  sitePage: (key, lang, adminView) =>
    client.get(`/site/pages/${key}`, { params: { lang, admin_view: adminView || undefined } }),
  updateSitePage: (key, payload) => client.put(`/site/pages/${key}`, payload),
  legalStatus: () => client.get("/site/legal/status"),
  legalPublish: (key, payload) => client.post(`/site/legal/${key}/publish`, payload || {}),
  legalVersions: (key) => client.get(`/site/legal/versions/${key}`),
  legalPending: () => client.get("/site/legal/pending"),
  legalAccept: (payload) => client.post("/site/legal/accept", payload),
  myAcceptances: () => client.get("/site/legal/my-acceptances"),
  siteFeatures: () => client.get("/site/features"),
  deleteAccount: (payload) => client.post("/auth/delete-account", payload),
  legalObject: (payload) => client.post("/site/legal/object", payload),
  legalObjections: (key) => client.get(`/site/legal/${key}/objections`),
  legalGiveNotice: (key, userId) => client.post(`/site/legal/${key}/objections/${userId}/notice`),
  legalWithdrawNotice: (key, userId) => client.delete(`/site/legal/${key}/objections/${userId}/notice`),
  cookieConfig: () => client.get("/site/cookie-config"),
  recordConsent: (payload) => client.post("/site/consent", payload),
  trackEvent: (payload) => client.post("/site/analytics/event", payload),
  // support & impersonation
  myServiceCode: () => client.get("/support/my-code"),
  rotateServiceCode: () => client.post("/support/rotate-code"),
  setWriteApproval: (approved) => client.post("/support/write-approval", { approved }),
  impersonate: (payload) => client.post("/support/impersonate", payload),
  supportSessions: () => client.get("/support/sessions"),
  // errors
  reportError: (payload) => client.post("/errors/report", payload),
  adminErrors: (params) => client.get("/errors", { params }),
  resolveError: (id) => client.post(`/errors/${id}/resolve`),
  errorSummary: () => client.get("/errors/summary"),
  // contracts & invites
  currentContract: () => client.get("/contracts/current"),
  acceptContract: (version) => client.post("/contracts/accept", { version }),
  contractVersions: () => client.get("/contracts/versions"),
  createContractVersion: (payload) => client.post("/contracts/versions", payload),
  createInvite: (payload) => client.post("/contracts/invites", payload),
  listInvites: () => client.get("/contracts/invites"),
  redeemInvite: (token) => client.post("/contracts/invites/redeem", { token }),
  // trials
  trialStatus: () => client.get("/investors/trial/status"),
  startTrial: (id) => client.post(`/investors/${id}/trial`),
  // posts
  posts: (params) => client.get("/posts", { params }),
  post: (slug) => client.get(`/posts/${slug}`),
  myPosts: () => client.get("/posts/mine/all"),
  createPost: (payload) => client.post("/posts", payload),
  updatePost: (id, payload) => client.put(`/posts/${id}`, payload),
  deletePost: (id) => client.delete(`/posts/${id}`),
  postComments: (slug) => client.get(`/posts/${slug}/comments`),
  addPostComment: (slug, payload) => client.post(`/posts/${slug}/comments`, payload),
  // public (no auth required)
  publicLeaderboard: () => client.get("/public/leaderboard"),
  publicPosts: () => client.get("/public/posts"),
  // Prices and the partner economics. Public because the marketing pages quote them
  // before anyone signs in — see lib/pricing.js.
  publicPricing: () => client.get("/public/pricing"),
  // investor payouts (Stripe Connect + Gutschrift)
  payoutSetup: () => client.get("/partner/payouts/me"),
  savePayoutProfile: (payload) => client.put("/partner/payouts/me", payload),
  payoutOnboard: () => client.post("/partner/payouts/onboard"),
  requestPayout: () => client.post("/partner/payouts/request"),
  // direct messages — the one conversation Full Access buys
  dmEligibility: () => client.get("/messages/eligibility"),
  dmConversations: () => client.get("/messages"),
  dmRequest: (investor_id, message) => client.post("/messages/request", { investor_id, message }),
  dmRespond: (id, accept) => client.post(`/messages/${id}/respond`, { accept }),
  dmThread: (id) => client.get(`/messages/${id}`),
  dmSend: (id, body) => client.post(`/messages/${id}/send`, { body }),
  dmAdminOverview: () => client.get("/messages/admin/overview"),
  dmAdminReset: (memberId) => client.post(`/messages/admin/${memberId}/reset`),
  // cancellation
  cancelReasons: () => client.get("/billing/cancel-reasons"),
  cancelFeedback: (payload) => client.post("/billing/cancel-feedback", payload),
  pauseSubscription: () => client.post("/billing/pause"),
  churn: () => client.get("/billing/churn"),
  // partner programme
  partnerMe: () => client.get("/partner/me"),
  setReferralHandle: (handle) => client.put("/partner/me/handle", { handle }),
  resolveReferral: (handle) => client.get(`/partner/resolve/${handle}`),
  claimReferral: (handle) => client.post("/partner/claim", { handle }),
  partnerSettings: () => client.get("/partner/settings"),
  updatePartnerSettings: (payload) => client.put("/partner/settings", payload),
  partnerPayouts: () => client.get("/partner/payouts"),
  markPayoutPaid: (id) => client.post(`/partner/payouts/${id}/mark-paid`),
  // identity + completion
  profile: () => client.get("/profile/me"),
  setUsername: (username) => client.put("/profile/username", { username }),
  checkUsername: (h) => client.get(`/profile/username-available/${h}`),
  setAvatar: (image) => client.put("/profile/avatar", { image }),
  clearAvatar: () => client.delete("/profile/avatar"),
  setCommentAs: (mode) => client.put("/profile/comment-as", { comment_as: mode }),
  // investor insight
  rankExplained: () => client.get("/investors/me/rank-explained"),
  postStats: () => client.get("/posts/mine/stats"),
  // applications + verification
  apply: (payload) => client.post("/applications", payload),
  myApplication: () => client.get("/applications/mine"),
  adminApplications: (status) => client.get("/admin/applications", { params: { status } }),
  applicationDocument: (id) => client.get(`/admin/applications/${id}/document`),
  decideApplication: (id, payload) => client.post(`/admin/applications/${id}/decide`, payload),
  submitVerification: (payload) => client.post("/verification", payload),
  myVerification: () => client.get("/verification/mine"),
  adminVerifications: (status) => client.get("/admin/verifications", { params: { status } }),
  verificationDocument: (id) => client.get(`/admin/verifications/${id}/document`),
  decideVerification: (id, payload) => client.post(`/admin/verifications/${id}/decide`, payload),
  // reactions
  reactions: (type, id) => client.get(`/reactions/${type}/${id}`),
  react: (type, id, emoji) => client.post(`/reactions/${type}/${id}`, { emoji }),
  usernamePolicy: () => client.get("/profile/username-policy"),
  // passkeys
  passkeys: () => client.get("/passkeys"),
  passkeyRegisterOptions: () => client.post("/passkeys/register/options"),
  passkeyRegisterVerify: (payload) => client.post("/passkeys/register/verify", payload),
  deletePasskey: (id) => client.delete(`/passkeys/${id}`),
  passkeyLoginOptions: (email) => client.post("/passkeys/login/options", { email }),
  passkeyLoginVerify: (payload) => client.post("/passkeys/login/verify", payload)
    .then(tracked("login", { method: "passkey" })),
  // discovery
  searchInvestors: (params) => client.get("/investors", { params }),
  bookmarkInvestor: (id) => client.post(`/investors/${id}/bookmark`),
  myBookmarks: () => client.get("/investors/me/bookmarks"),
  upvoteInvestor: (id) => client.post(`/investors/${id}/upvote`),
};

export const downloadExport = async (kind, format, params = {}) => {
  const lang = localStorage.getItem("costock_lang") || "en";
  const locale = localStorage.getItem("costock_numlocale") || (lang === "de" ? "de-DE" : "en-US");
  const qs = new URLSearchParams({ format, lang, locale, ...params }).toString();
  // credentials: "include" is the fetch equivalent of withCredentials — the session
  // cookie goes with it and there is no header to build.
  let res = await fetch(`${API}/export/${kind}?${qs}`, { credentials: "include" });
  if (res.status === 401) {
    // Exports are the one place a stale access cookie is likely: they are often the
    // first thing clicked after leaving a tab open. Renew once and retry.
    await refreshSession();
    res = await fetch(`${API}/export/${kind}?${qs}`, { credentials: "include" });
  }
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `costock_${kind}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  track("export", { export_type: kind, file_format: format });
};

export default client;
